// stores/breakoutRoomStore.ts
import { create } from 'zustand';
import { BreakoutRoomService } from '../service/breakoutroom.service';
import { jitsiService } from '../../../shared/singletons/jitsi-singleton';
import { useMeetingStore, useWorkplaceStore } from '@/host/stores';
import {
  trackBreakoutConfigured,
  trackBreakoutEnded,
  trackBreakoutStarted,
} from '@host/utils/breakout-tracking';

// Tracks the latest loadSessionsFromRoomId request id per room. The backend can send
// multiple breakoutSessionCreated/breakoutSessionEnded events for the same transition
// (one per affected participant, all broadcast to every client), each triggering its
// own force-reload — with no sequencing, an OLDER request (still reflecting
// "in-progress" data fetched before the backend had fully persisted the end) can
// resolve AFTER a newer request that correctly shows "ended", overwriting the store
// back to "still in-progress" and causing FloatingMeetingView to switch the
// participant right back into the breakout room it had just correctly left. Only the
// response matching the latest request id for that room is applied; older ones are
// dropped as stale.
const latestLoadRequestId = new Map<string, number>();

const withTimeout = async <T>(
  promise: Promise<T> | undefined,
  label: string,
  timeoutMs: number = 5000
): Promise<T | undefined> => {
  if (!promise) return undefined;

  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race<T | undefined>([
      promise,
      new Promise<undefined>((resolve) => {
        timeoutId = setTimeout(() => {
          console.warn(`[Breakout] Timed out while waiting for ${label}`);
          resolve(undefined);
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};
// breakoutRoomGroups holds every group for the room (participants need them to see their own
// assignment), so management actions must check ownership explicitly rather than relying on
// the list only containing groups this user created.
const isGroupOwnedByCurrentUser = (group?: { creatorUserPid: string }): boolean => {
  const currentUserPid = useWorkplaceStore.getState().user?.pid;
  return !!group && !!currentUserPid && group.creatorUserPid === currentUserPid;
};

export interface BreakoutRoom {
  roomName: string;
  jitsiRoomJid: string;
  participants: string[];
  users: string[];
  userDetails?: Record<
    string,
    { displayName: string; avatarUrl: string; isLocal: boolean; id: string }
  >;
}

export interface BreakoutRoomGroup {
  sessionGroupId: string;
  mainRoomId: string;
  durationMinutes: number;
  rooms: BreakoutRoom[];
  creatorUserPid: string;
  createdAt?: string;
  expiresAt?: string;
  status?: 'not-started' | 'in-progress' | 'completed';
}

interface BreakoutRoomState {
  currentBreakoutRoomGroups: BreakoutRoomGroup[];
  sessionStarting: boolean;
  sessionEnding: boolean;
  breakoutRoomService: BreakoutRoomService;
  endSession: (jitsiRoomId: string) => Promise<void>;
  endAllSessions: (mainRoomId: string, reason?: 'manual' | 'timer') => Promise<void>;
  switchMyRoom: (mainRoom: string) => Promise<void>;
  switchMainRoom: (mainRoomJid: string) => Promise<void>;
  // State
  breakoutRoomGroups: BreakoutRoomGroup[];
  // Actions
  addBreakoutRoomGroup: (
    numberOfSessions: number,
    timeInMinutes: number,
    creationMethod: 'manual' | 'automatic',
    mainRoomId: string
  ) => void;
  removeBreakoutRoomGroup: (groupId: string) => void;
  getBreakoutRoomGroupById: (groupId: string) => BreakoutRoomGroup | undefined;
  getAllBreakoutRoomGroups: () => BreakoutRoomGroup[];
  getBreakoutRoomGroupByRoomId: (roomId: string) => BreakoutRoomGroup | undefined;
  getBreakoutRoomGroupByJitsiRoomId: (jitsiRoomId: string) => BreakoutRoomGroup | undefined;
  setRoomName: (groupId: string, roomId: string, name: string) => void;
  addParticipantToRoom: (
    roomId: string,
    breakoutRoomId: string,
    participantId: string,
    userId: string
  ) => void;
  removeParticipantFromRoom: (roomId: string, participantId: string) => void;
  removeParticipantFromAnyRoom: (participantId: string) => void;
  removeRoomById: (roomId: string) => void;
  startBreakoutSessionsForGroup: (groupId: BreakoutRoomGroup) => void;
  loadSessionsFromRoomId: (roomId: string, force?: boolean) => Promise<void>;
  fetchedRooms: string[];
  // Everyone ever seen in a main room's live Jitsi roster, keyed by mainRoomId then
  // participantId. Once a moderator is assigned into a breakout room, their own
  // client's live roster (useMeetingStore.jitsiParticipants) switches to reflect that
  // room's occupants instead of the main room's — so anyone left behind in the main
  // room (e.g. an unassigned participant) becomes invisible to every live lookup.
  // This cache survives this client's own room switches, but evicts anyone who really
  // left (a Jitsi USER_LEFT outside a room switch) so departed users don't linger.
  knownMainRoomParticipants: Record<string, Record<string, any>>;
  captureMainRoomParticipants: (
    mainRoomId: string,
    participants: Record<string, any>,
    departedIds?: string[]
  ) => void;
}

const useBreakoutRoomStore = create<BreakoutRoomState>((set, get) => ({
  sessionStarting: false,
  sessionEnding: false,
  fetchedRooms: [],
  currentBreakoutRoomGroups: [],
  breakoutRoomGroups: [],
  knownMainRoomParticipants: {},
  captureMainRoomParticipants: (mainRoomId, participants, departedIds = []) => {
    const hasParticipants = !!participants && Object.keys(participants).length > 0;
    if (!mainRoomId || (!hasParticipants && departedIds.length === 0)) return;
    set((state) => {
      const known = { ...state.knownMainRoomParticipants[mainRoomId] };
      departedIds.forEach((id) => delete known[id]);
      return {
        knownMainRoomParticipants: {
          ...state.knownMainRoomParticipants,
          [mainRoomId]: { ...known, ...participants },
        },
      };
    });
  },
  breakoutRoomService: new BreakoutRoomService(),
  endSession: async (jitsiRoomId: string) => {
    const group = get().getBreakoutRoomGroupByJitsiRoomId(jitsiRoomId);
    if (!group || !isGroupOwnedByCurrentUser(group)) return;
    const room = group.rooms.find((r) => r.jitsiRoomJid === jitsiRoomId);
    if (!room) return;
    await get().breakoutRoomService.endSession(jitsiRoomId, room.users);

    try {
      await jitsiService?.removeBreakoutRoom?.(jitsiRoomId);
    } catch (error) {
      console.log('Error removing breakout room from Jitsi:', error);
    }
    await get().loadSessionsFromRoomId(group.mainRoomId);
  },
  removeRoomById: (roomId: string) => {
    set((state) => ({
      breakoutRoomGroups: state.breakoutRoomGroups.map((group) =>
        isGroupOwnedByCurrentUser(group)
          ? { ...group, rooms: group.rooms.filter((room) => room.jitsiRoomJid !== roomId) }
          : group
      ),
    }));
  },

  switchMyRoom: async (jitsiRoomId: string) => {
    await jitsiService?.switchToRoom(jitsiRoomId);
  },

  switchMainRoom: async (mainRoomJid: string) => {
    await jitsiService?.switchToRoom(mainRoomJid || '');
  },

  endAllSessions: async (mainRoomId: string, reason: 'manual' | 'timer' = 'manual') => {
    const groups = get().breakoutRoomGroups.filter(
      (group) =>
        group.mainRoomId === mainRoomId &&
        group.status === 'in-progress' &&
        isGroupOwnedByCurrentUser(group)
    );
    if (groups.length === 0) return;

    set({ sessionEnding: true });

    try {
      const mainRoomJid =
        (await withTimeout(jitsiService?.getMainRoom(), 'main room lookup')) || '';

      for (const group of groups) {
        const allUsers = group.rooms.flatMap((room) => room.users);
        await withTimeout(
          get().breakoutRoomService.endAllSessions(group.mainRoomId, mainRoomJid, allUsers),
          `persisting breakout end for ${group.mainRoomId}`
        );

        await Promise.allSettled(
          group.rooms.flatMap((room) =>
            room.participants.map(async (participantId) => {
              try {
                const participantJid = jitsiService?.getParticipantJidById(participantId);
                if (participantJid) {
                  await withTimeout(
                    jitsiService?.sendParticipantToMainRoom?.(participantJid),
                    `returning participant ${participantId} to main room`
                  );
                } else {
                  console.log(
                    "[BREAKOUT-DIAG] endAllSessions sendParticipantToMainRoom SKIPPED - no participantJid found (participant likely not visible in this client's live Jitsi roster)",
                    { participantId }
                  );
                }
              } catch (error) {
                console.log('Error returning participant to main room:', error);
              }
            })
          )
        );

        await Promise.allSettled(
          group.rooms.map(async (room) => {
            try {
              await withTimeout(
                jitsiService?.removeBreakoutRoom?.(room.jitsiRoomJid),
                `removing breakout room ${room.jitsiRoomJid}`
              );
            } catch (error) {
              console.log('Error removing breakout room from Jitsi:', error);
            }
          })
        );
      }
      trackBreakoutEnded(groups, reason);
    } finally {
      set({ sessionEnding: false, breakoutRoomGroups: [], currentBreakoutRoomGroups: [] });
    }
  },
  loadSessionsFromRoomId: async (roomId: string, force: boolean = false) => {
    // specific check to avoid multiple API calls for the same room
    const alreadyFetched = get().fetchedRooms.includes(roomId);
    if (!force && alreadyFetched) {
      return;
    }

    const requestId = (latestLoadRequestId.get(roomId) ?? 0) + 1;
    latestLoadRequestId.set(roomId, requestId);

    const breakoutRoomService = get().breakoutRoomService;
    const data = await breakoutRoomService.getBreakoutRoomsForMainRoom(roomId);

    // A newer loadSessionsFromRoomId call for this room has started since this one
    // began — its response (whenever it lands) is authoritative, and applying this
    // older/slower response now would overwrite already-correct state with stale
    // data (see latestLoadRequestId comment above). Drop it.
    if (latestLoadRequestId.get(roomId) !== requestId) {
      return;
    }

    // Mark room as fetched regardless of result to prevent re-fetching empty rooms
    set((state) => ({
      fetchedRooms: state.fetchedRooms.includes(roomId)
        ? state.fetchedRooms
        : [...state.fetchedRooms, roomId],
    }));

    if (data && data.length > 0) {
      // Group sessions by session_group_id
      const groupedSessions = data.reduce(
        (acc: any, session: any) => {
          const groupId = session.session_group_id;
          if (!acc[groupId]) {
            acc[groupId] = [];
          }
          acc[groupId].push(session);
          return acc;
        },
        {} as Record<string, typeof data>
      );

      // Map backend status to frontend status
      const mapStatus = (
        status: 'active' | 'closed' | 'expired'
      ): 'not-started' | 'in-progress' | 'completed' => {
        if (status === 'active') return 'in-progress';
        if (status === 'closed' || status === 'expired') return 'completed';
        return 'not-started';
      };

      // Transform to BreakoutRoomGroup format
      const transformedGroups: BreakoutRoomGroup[] = Object.entries(groupedSessions).map(
        ([groupId, sessions]: [string, typeof data]) => {
          const firstSession = sessions[0];

          const rooms: BreakoutRoom[] = sessions.map((session: any) => {
            // Check if we already have this room in critical store to preserve user details
            let existingGroup = get().currentBreakoutRoomGroups.find(
              (g) => g.sessionGroupId === groupId
            );

            if (!existingGroup) {
              existingGroup = get().breakoutRoomGroups.find((g) => g.sessionGroupId === groupId);
            }

            const existingRoom = existingGroup?.rooms.find(
              (r) => r.jitsiRoomJid === session.jitsi_room_jid
            );

            return {
              roomName: session.room_name,
              jitsiRoomJid: session.jitsi_room_jid,
              participants: session.participants || [],
              users: session.userIds || [],
              // Backend-persisted names (visible to every client) merged with
              // this client's own in-memory cache (covers not-yet-persisted moves).
              userDetails: {
                ...(session.metadata?.userDetails || {}),
                ...(existingRoom?.userDetails || {}),
              },
            };
          });

          return {
            sessionGroupId: groupId,
            mainRoomId: firstSession.main_room_id,
            durationMinutes: firstSession.duration_minutes,
            rooms,
            creatorUserPid: firstSession.creator_user_pid,
            createdAt: firstSession.created_at,
            expiresAt: firstSession.expires_at,
            status: mapStatus(firstSession.status),
          };
        }
      );

      set(() => ({
        currentBreakoutRoomGroups: transformedGroups,
        breakoutRoomGroups: transformedGroups,
      }));
    } else {
      set(() => ({
        currentBreakoutRoomGroups: [],
      }));
    }
  },

  addBreakoutRoomGroup: (numberOfSessions, timeInMinutes, creationMethod, mainRoomId) => {
    const jitsiParticipants = useMeetingStore.getState().jitsiParticipants;
    const participantIds = Object.values(jitsiParticipants);

    // Shuffle participants using Fisher-Yates algorithm
    const shuffleArray = <T>(array: T[]): T[] => {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    const newGroupId = `session-group-${mainRoomId}`;
    const workplaceStore = useWorkplaceStore.getState();
    const currentUser = workplaceStore.user;

    // Check if a group with this mainRoomId already exists
    const existingGroup = get().breakoutRoomGroups.find((group) => group.mainRoomId === mainRoomId);
    // Another user's group for this room must not be replaced from this client.
    if (existingGroup && !isGroupOwnedByCurrentUser(existingGroup)) return;

    let newRooms: BreakoutRoom[] = [];

    if (existingGroup && creationMethod === 'manual') {
      // Preserve existing configuration for manual mode
      const existingRooms = existingGroup.rooms;
      const existingRoomCount = existingRooms.length;

      if (numberOfSessions <= existingRoomCount) {
        // Keep only the first N rooms (remove from the bottom)
        newRooms = existingRooms.slice(0, numberOfSessions).map((room) => ({
          ...room,
        }));
      } else {
        // Keep all existing rooms and add new empty ones
        newRooms = existingRooms.map((room) => ({ ...room }));

        for (let i = existingRoomCount; i < numberOfSessions; i++) {
          newRooms.push({
            roomName: `Breakout Room ${i + 1}`,
            jitsiRoomJid: `${mainRoomId}-breakout-${i + 1}`,
            participants: [],
            users: [],
            userDetails: {},
          });
        }
      }
    } else {
      // Create fresh rooms for automatic mode or when no existing group
      for (let i = 0; i < numberOfSessions; i++) {
        newRooms.push({
          roomName: `Breakout Room ${i + 1}`,
          jitsiRoomJid: `${mainRoomId}-breakout-${i + 1}`,
          participants: [],
          users: [],
          userDetails: {},
        });
      }

      // Distribute shuffled participants evenly across rooms for automatic mode
      if (creationMethod === 'automatic') {
        const shuffledParticipants = shuffleArray(participantIds.map((p) => p.id));

        shuffledParticipants.forEach((participantId, index) => {
          const roomIndex = index % numberOfSessions;
          const participant = jitsiParticipants[participantId];
          newRooms[roomIndex].participants.push(participantId);
          newRooms[roomIndex].users.push(jitsiParticipants[participantId].userPid || '');
          if (participant) {
            if (!newRooms[roomIndex].userDetails) newRooms[roomIndex].userDetails = {}; // Ensure defined
            newRooms[roomIndex].userDetails![participantId] = {
              displayName: participant.displayName,
              avatarUrl: participant.avatarUrl,
              isLocal: participant.isLocal,
              id: participant.id,
            };
          }
        });
      }
    }

    const newGroup: BreakoutRoomGroup = {
      sessionGroupId: newGroupId,
      mainRoomId,
      durationMinutes: timeInMinutes,
      rooms: newRooms,
      creatorUserPid: currentUser?.pid || '',
    };

    const otherGroups = get().breakoutRoomGroups.filter((group) => group.mainRoomId !== mainRoomId);

    set(() => ({
      breakoutRoomGroups: [...otherGroups, newGroup],
    }));
    trackBreakoutConfigured({
      roomCount: numberOfSessions,
      plannedMinutes: timeInMinutes,
      assignment: creationMethod,
      participantCount: participantIds.length,
    });
  },
  removeBreakoutRoomGroup: (groupId) =>
    set((state) => ({
      breakoutRoomGroups: state.breakoutRoomGroups.filter(
        (group) => group.sessionGroupId !== groupId
      ),
    })),
  getBreakoutRoomGroupByJitsiRoomId: (jitsiRoomId) => {
    const groups = get().breakoutRoomGroups;
    for (const group of groups) {
      const room = group.rooms.find((r) => r.jitsiRoomJid === jitsiRoomId);
      if (room) {
        return group;
      }
    }
    return undefined;
  },
  getBreakoutRoomGroupById: (groupId) => {
    return get().breakoutRoomGroups.find((group) => group.sessionGroupId === groupId);
  },
  getAllBreakoutRoomGroups: () => {
    return get().breakoutRoomGroups;
  },
  getBreakoutRoomGroupByRoomId: (roomId) => {
    const groups = get().breakoutRoomGroups;
    return groups.find((group) => group.mainRoomId === roomId);
  },
  setRoomName: (groupId, roomId, name) => {
    set((state) => ({
      breakoutRoomGroups: state.breakoutRoomGroups.map((group) => {
        if (group.sessionGroupId === groupId && isGroupOwnedByCurrentUser(group)) {
          return {
            ...group,
            rooms: group.rooms.map((room) =>
              room.jitsiRoomJid === roomId ? { ...room, roomName: name } : room
            ),
          };
        }
        return group;
      }),
    }));
  },
  addParticipantToRoom: (roomId, breakoutRoomId, participantId, userId) => {
    const targetGroup = get().breakoutRoomGroups.find((group) => group.mainRoomId === roomId);
    if (!isGroupOwnedByCurrentUser(targetGroup)) return;

    //check if participant already exists in any room
    const existingRoom = get()
      .breakoutRoomGroups.flatMap((group) => group.rooms)
      .find((r) => r.participants.includes(participantId));
    if (existingRoom) {
      // Already in the target room — nothing to do
      if (existingRoom.jitsiRoomJid === breakoutRoomId) {
        return;
      }
      // Move: remove from existing room, then add to new room in one update
      const existingRoomId = existingRoom.jitsiRoomJid;
      const participantIndex = existingRoom.participants.indexOf(participantId);
      // Preserve the userId from the existing room (userId param may be empty when dragging from a breakout room)
      const resolvedUserId = userId || existingRoom.users[participantIndex] || '';
      const jitsiParticipants = useMeetingStore.getState().jitsiParticipants;
      const participant =
        jitsiParticipants[participantId] ||
        get().knownMainRoomParticipants[roomId]?.[participantId];
      // Preserve userDetails from the existing room if not in jitsiParticipants (e.g. user left main room)
      const cachedDetail = existingRoom.userDetails?.[participantId];
      const userDetailEntry = participant
        ? {
            [participantId]: {
              displayName: participant.displayName,
              avatarUrl: participant.avatarUrl,
              isLocal: participant.isLocal,
              id: participant.id,
            },
          }
        : cachedDetail
          ? { [participantId]: cachedDetail }
          : {};

      set((state) => ({
        breakoutRoomGroups: state.breakoutRoomGroups.map((group) => {
          if (group.mainRoomId === roomId) {
            return {
              ...group,
              rooms: group.rooms.map((room) => {
                if (room.jitsiRoomJid === existingRoomId) {
                  const idx = room.participants.indexOf(participantId);
                  return {
                    ...room,
                    participants: room.participants.filter((id) => id !== participantId),
                    users: room.users.filter((_, index) => index !== idx),
                    userDetails: Object.fromEntries(
                      Object.entries(room.userDetails || {}).filter(
                        ([key]) => key !== participantId
                      )
                    ),
                  };
                }
                if (room.jitsiRoomJid === breakoutRoomId) {
                  return {
                    ...room,
                    participants: [...room.participants, participantId],
                    users: [...room.users, resolvedUserId],
                    userDetails: { ...room.userDetails, ...userDetailEntry },
                  };
                }
                return room;
              }),
            };
          }
          return group;
        }),
      }));
      return;
    }

    const jitsiParticipants = useMeetingStore.getState().jitsiParticipants;
    // Fall back to the main-room cache so the name still gets persisted when this
    // client can't see the participant live (e.g. it's switched into a breakout room).
    const participant =
      jitsiParticipants[participantId] || get().knownMainRoomParticipants[roomId]?.[participantId];
    const userDetailEntry = participant
      ? {
          [participantId]: {
            displayName: participant.displayName,
            avatarUrl: participant.avatarUrl,
            isLocal: participant.isLocal,
            id: participant.id,
          },
        }
      : {};

    set((state) => ({
      breakoutRoomGroups: state.breakoutRoomGroups.map((group) => {
        if (group.mainRoomId === roomId) {
          return {
            ...group,
            rooms: group.rooms.map((room) =>
              room.jitsiRoomJid === breakoutRoomId
                ? {
                    ...room,
                    participants: [...room.participants, participantId],
                    users: [...room.users, userId],
                    userDetails: { ...room.userDetails, ...userDetailEntry },
                  }
                : room
            ),
          };
        }
        return group;
      }),
    }));
  },
  removeParticipantFromRoom: (roomId, participantId) => {
    const room = get()
      .breakoutRoomGroups.flatMap((group) => group.rooms)
      .find((r) => r.jitsiRoomJid === roomId);
    if (!room) return;
    const owningGroup = get().breakoutRoomGroups.find((group) =>
      group.rooms.some((r) => r.jitsiRoomJid === roomId)
    );
    if (!owningGroup || !isGroupOwnedByCurrentUser(owningGroup)) return;
    const groupId = owningGroup.sessionGroupId;

    set((state) => ({
      breakoutRoomGroups: state.breakoutRoomGroups.map((group) => {
        if (group.sessionGroupId === groupId) {
          return {
            ...group,
            rooms: group.rooms.map((room) => {
              if (room.jitsiRoomJid !== roomId) {
                return room;
              }

              const participantIndex = room.participants.indexOf(participantId);

              return {
                ...room,
                participants: room.participants.filter((id) => id !== participantId),
                users: room.users.filter((_, index) => index !== participantIndex),
                userDetails: Object.fromEntries(
                  Object.entries(room.userDetails || {}).filter(([key]) => key !== participantId)
                ),
              };
            }),
          };
        }
        return group;
      }),
    }));
  },
  removeParticipantFromAnyRoom: (participantId) => {
    set((state) => ({
      breakoutRoomGroups: state.breakoutRoomGroups.map((group) => ({
        ...group,
        rooms: group.rooms.map((room) => {
          if (room.participants.includes(participantId)) {
            const participantIndex = room.participants.indexOf(participantId);
            return {
              ...room,
              participants: room.participants.filter((id) => id !== participantId),
              users: room.users.filter((_, index) => index !== participantIndex),
              userDetails: Object.fromEntries(
                Object.entries(room.userDetails || {}).filter(([key]) => key !== participantId)
              ),
            };
          }
          return room;
        }),
      })),
    }));
  },
  startBreakoutSessionsForGroup: async (group: BreakoutRoomGroup) => {
    if (!group || !isGroupOwnedByCurrentUser(group)) return;
    set({ sessionStarting: true });
    try {
      await Promise.all(
        group.rooms.map(async (room) => {
          const breakoutRoomId = await jitsiService?.createBreakoutRoom(room.roomName);
          room.jitsiRoomJid = breakoutRoomId || '';
        })
      );

      const request = {
        mainRoomId: group.mainRoomId,
        sessionGroupId: group.sessionGroupId,
        durationMinutes: group.durationMinutes,
        creatorUserPid: group.creatorUserPid,
        metadata: {
          participants: group.rooms.flatMap((room) => room.participants),
          users: group.rooms.flatMap((room) => room.users),
        },
      };

      set((state) => ({
        breakoutRoomGroups: state.breakoutRoomGroups.map((g) => {
          if (g.sessionGroupId === group.sessionGroupId) {
            return { ...group };
          }
          return g;
        }),
      }));

      await get().breakoutRoomService.createBreakoutRoom(request, group);
      trackBreakoutStarted(group);

      for (const room of group.rooms) {
        if (!room.jitsiRoomJid) continue;
        for (const participantId of room.participants) {
          try {
            const participantJid = jitsiService?.getParticipantJidById(participantId);
            if (participantJid) {
              await jitsiService?.sendParticipantToBreakoutRoom(participantJid, room.jitsiRoomJid);
            }
          } catch (error) {
            console.error(
              `Error sending participant ${participantId} to breakout room ${room.jitsiRoomJid}:`,
              error
            );
          }
        }
      }

      await get()
        .loadSessionsFromRoomId(group.mainRoomId, true)
        .catch((error: any) => {
          console.error('Error loading breakout sessions after creation:', error);
        });
    } catch (error) {
      console.error('Failed to start breakout sessions:', error);
    } finally {
      set({ sessionStarting: false });
    }
  },
}));

// Subscribe to useMeetingStore to automatically remove participants from breakout rooms
// when they leave the main meeting (browser close, logout, disconnect, etc.)
// Skip removal when a breakout session is starting or in-progress, because participants
// temporarily leave the main conference when they move to a breakout room.
useMeetingStore.subscribe((state, prevState) => {
  const { sessionStarting, breakoutRoomGroups, captureMainRoomParticipants } =
    useBreakoutRoomStore.getState();

  // Remember everyone seen in this client's live roster, keyed by the current main
  // room, so this client can still list them later even after its own Jitsi room
  // switches away (see knownMainRoomParticipants above). A room switch wipes the roster
  // while isRoomSwitchInProgress() is true, so skip those updates; outside a switch, a
  // participant only drops out of the roster via USER_LEFT, i.e. they really left.
  const mainRoomId = useWorkplaceStore.getState().currentRoom?.pid;
  if (mainRoomId && !jitsiService?.isRoomSwitchInProgress()) {
    const departedIds = Object.keys(prevState.jitsiParticipants).filter(
      (id) => !state.jitsiParticipants[id]
    );
    captureMainRoomParticipants(mainRoomId, state.jitsiParticipants, departedIds);
  }

  // Don't remove participants while sessions are starting or any session is active
  const hasActiveSession = breakoutRoomGroups.some((g) => g.status === 'in-progress');
  if (sessionStarting || hasActiveSession) return;

  const currentIds = Object.keys(state.jitsiParticipants);
  const prevIds = Object.keys(prevState.jitsiParticipants);

  // If the number of participants has decreased, find who left
  if (currentIds.length < prevIds.length) {
    const removedIds = prevIds.filter((id) => !currentIds.includes(id));

    // Remove each departed participant from any breakout room they were assigned to
    removedIds.forEach((id) => {
      useBreakoutRoomStore.getState().removeParticipantFromAnyRoom(id);
    });
  }
});

// Non-moderator clients never call startBreakoutSessionsForGroup/endAllSessions themselves,
// so they rely on the broadcasted breakoutSessionCreated/breakoutSessionEnded window events
// (dispatched by BreakoutRoomEventRouter) to know when to show the "session starting/ending" popup
// and to force-refresh currentBreakoutRoomGroups (loadSessionsFromRoomId is otherwise cached per
// room via fetchedRooms, so without a forced reload here their room list only updates on refresh).
let sessionStartingTimeout: ReturnType<typeof setTimeout> | undefined;
window.addEventListener('breakoutSessionCreated', (event: Event) => {
  const mainRoomId =
    (event as CustomEvent).detail?.room_pid || (event as CustomEvent).detail?.meeting_pid;

  clearTimeout(sessionStartingTimeout);
  useBreakoutRoomStore.setState({ sessionStarting: true });
  if (mainRoomId) {
    useBreakoutRoomStore.getState().loadSessionsFromRoomId(mainRoomId, true);
  } else {
    console.log(
      '[BREAKOUT-DIAG] breakoutSessionCreated had no room_pid/meeting_pid — loadSessionsFromRoomId NOT called'
    );
  }
  sessionStartingTimeout = setTimeout(() => {
    useBreakoutRoomStore.setState({ sessionStarting: false });
  }, 5000);
});

let sessionEndingTimeout: ReturnType<typeof setTimeout> | undefined;
window.addEventListener('breakoutSessionEnded', (event: Event) => {
  const mainRoomId =
    (event as CustomEvent).detail?.room_pid || (event as CustomEvent).detail?.meeting_pid;

  clearTimeout(sessionEndingTimeout);
  useBreakoutRoomStore.setState({ sessionEnding: true });
  if (mainRoomId) {
    useBreakoutRoomStore.getState().loadSessionsFromRoomId(mainRoomId, true);
  } else {
    console.log(
      '[BREAKOUT-DIAG] breakoutSessionEnded had no room_pid/meeting_pid — loadSessionsFromRoomId NOT called'
    );
  }
  sessionEndingTimeout = setTimeout(() => {
    useBreakoutRoomStore.setState({ sessionEnding: false });
  }, 5000);
});

export default useBreakoutRoomStore;
